import type { components } from '@/types/api/v1'
import type { IServerSideGetRowsRequest } from 'ag-grid-community'
import { fetchClient } from '../../typedClient'
import {
  type DataPerspective,
  type OrderGridRow,
  toGridRow,
} from './transformers'

// Types inferred from OpenAPI schema
export type IntegrationStatus = components['schemas']['IntegrationStatus']

/** One leaf row of the orders grid. Taken off the schema rather than off a
 * response type -- /order/find wraps it in the generic paginated envelope. */
export type OrderOverviewRow = components['schemas']['OrderOverviewRow']

export type OrderPerspectiveView = OrderOverviewRow['internal_order']

export type ProductRatio = NonNullable<
  OrderPerspectiveView['product_ratios']
>[number]
export type CrossroadsRoutingInfo = NonNullable<OrderPerspectiveView['routing']>

export type OrderProgressionState =
  components['schemas']['OrderProgressionState']

/** A set-filter dropdown option: the stored value the filter matches on, plus
 * the text the user reads. */
export type FilterValue = components['schemas']['FilterValue']

/** Rows per request. Matches the grid's paginationPageSize so one page is
 * one fetch. */
export const ORDER_PAGE_SIZE = 100

/**
 * Everything the request varies by that isn't paging, sorting or filtering:
 * the perspective the rows are shaped for, plus the business filters the
 * endpoint has always taken as their own fields (the date window keeps the
 * query index-eligible, so it doesn't move into `filterModel`).
 */
export interface OrderOverviewServerFilters {
  dateRange: { start: string; end: string }
  /** Rows are shaped per perspective, so switching has to refetch. */
  perspective: DataPerspective
  status?: IntegrationStatus[]
  progressionState?: OrderProgressionState[]
  partnerTenantIds?: string[]
  /** Test/Prod, resolved server-side against the tenant's own instances.
   * Undefined means every instance. */
  mode?: components['schemas']['EnvMode']
}

type OrderFindRow =
  | OrderOverviewRow
  | (components['schemas']['GroupRow'] & Record<string, unknown>)

/** SSRM group rows are `{ <groupField>: key, childCount }` with no leaf data,
 * so they can't be flattened to a perspective -- they go through untouched. */
function isGroupRow(
  row: OrderFindRow,
): row is components['schemas']['GroupRow'] {
  return 'childCount' in row
}

/**
 * SSRM datasource fetch for the orders grid -- a plain function, not a hook
 * (see useServerSideDataSource; its react-query key includes `fetchFn.name`,
 * so this has to stay a named top-level function).
 */
export async function fetchOrderFind({
  filters,
  search,
  request,
}: {
  filters: OrderOverviewServerFilters
  search: string
  request: IServerSideGetRowsRequest & { includeCount?: boolean }
}): Promise<{ rowData: OrderGridRow[]; rowCount: number }> {
  const { data, error } = await fetchClient.POST('/crossroads/order/find', {
    body: {
      ...orderFindBody(filters, request),
      search,
      includeCount: request.includeCount ?? true,
    },
  })
  if (error) throw error

  return {
    rowData: (data.rowData as OrderFindRow[]).map((row) =>
      isGroupRow(row)
        ? (row as unknown as OrderGridRow)
        : toGridRow(row, filters.perspective),
    ),
    rowCount: data.rowCount,
  }
}

/** Distinct values for a column's set filter dropdown. The search term goes
 * along so the dropdown narrows with it rather than offering values that
 * aren't on screen. */
export async function fetchOrderFilterValues(
  column: string,
  filters: OrderOverviewServerFilters,
  filterModel: IServerSideGetRowsRequest['filterModel'],
  search = '',
): Promise<FilterValue[]> {
  const { data, error } = await fetchClient.POST(
    '/crossroads/order/filter_values',
    {
      params: { query: { column } },
      body: {
        ...orderBusinessFilters(filters),
        startRow: 0,
        endRow: 0,
        includeCount: false,
        filterModel: filterModel ?? {},
        search,
      },
    },
  )
  if (error) throw error
  return data
}

function orderBusinessFilters(filters: OrderOverviewServerFilters) {
  return {
    dateRange: filters.dateRange,
    status: filters.status,
    progressionState: filters.progressionState,
    partnerTenantIds: filters.partnerTenantIds,
    mode: filters.mode,
  }
}

function orderFindBody(
  filters: OrderOverviewServerFilters,
  request: IServerSideGetRowsRequest,
) {
  return {
    ...orderBusinessFilters(filters),
    startRow: request.startRow ?? 0,
    endRow: request.endRow ?? ORDER_PAGE_SIZE,
    sortModel: (request.sortModel ?? []) as {
      colId: string
      sort: 'asc' | 'desc'
    }[],
    filterModel: request.filterModel ?? {},
    rowGroupCols: (request.rowGroupCols ?? []).flatMap((col) =>
      col.field
        ? [{ id: col.id, displayName: col.displayName, field: col.field }]
        : [],
    ),
    groupKeys: request.groupKeys ?? [],
  }
}
